// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

// import { ITrainRecordProps } from "../react/components/pages/train/trainRecord";

import { IAssetPreviewSettings } from "../react/components/common/assetPreviewimg/assetPreview";

/**
 * @name - Application State
 * @description - Defines the root level application state
 * @member appSettings - Application wide settings
 * @member connections - Global list of connections available to application
 * @member recentProjects - List of recently used projects
 * @member currentProject - The active project being edited
 * @member appError - error in the app if any
 */

export interface IUserEndPoint {
    _id?: string;
    active_status?: any;
    category?: string;
    endpoint?: string;
    endpoint_type?: string;
    endpoint_name?: string;
    project_name: string;
    updated_date?: string;
 }

export interface IGetUserEndPoint {
    project_name: string;
    endpoint_type: string;
    endpoint_name?: string;
    active_status?: string;
 }

export interface IApplicationState {
    appSettings: IAppSettings;
    connections: IConnection[];
    recentProjects: IProject[];
    loadModels?: any[];
    uploadFilesCount?: number;
    currentProject: IProject;
    appError?: IAppError;
    loadAllFiles?: IAsset[];
    loadTestFiles?: IAsset[];
    appTitle?: string;
    loggedInUser: any;
    selectedOcrFiles?: IOcrContent[];
    logOutUser: boolean;
    userQueues?: any[];
    loadOPTestFiles?: IAsset[];
    prebuiltSettings?: IPrebuiltSettings;
}

export interface IUploadFileStatus {
    status: boolean;
    statusString: string;
}

/**
 * ocrContent Values
 */
export interface IOcrContent {
    analyzeResult: any;
    createdDateTime: string;
    lastUpdatedDateTime: string;
    status: string;
    id?: string;
    fileName?: string;
}

/**
 * @name - Application Error
 * @description - Defines error detail
 * @member title - title of the error to display
 * @member message - message of the error to display
 * @member errorCode - error category
 */
export interface IAppError {
    errorCode: ErrorCode;
    message: any;
    title?: string;
}

/**
 * Base application error
 */
export class AppError extends Error implements IAppError {
    public errorCode: ErrorCode;
    public message: string;
    public title?: string;

    constructor(errorCode: ErrorCode, message: string, title: string = null) {
        super(message);
        this.errorCode = errorCode;
        this.message = message;
        this.title = title;
        console.log(this.errorCode);
        console.log(this.message);
        console.log(this.title);
    }
}

/**
 * @name - Provider Options
 * @description - Property map of key values used within a export / asset / storage provider
 */
export interface IProviderOptions {
    [key: string]: any;
}

/**
 * @name - Application settings
 * @description - Defines the root level configuration options for the application
 * @member devToolsEnabled - Whether dev tools are current open and enabled
 * @member securityTokens - Token used to encrypt sensitive project settings
 */
export interface IAppSettings {
    securityTokens: ISecurityToken[];
    thumbnailSize?: ISize;
    hideUploadingOption?: boolean;
}


/**
 * @name - Project
 * @description - Defines the structure of a tagging project
 * @member id - Unique identifier
 * @member name - User defined name
 * @member securityToken - The Base64 encoded token used to encrypt sensitive project data
 * @member description - User defined description
 * @member tags - User defined list of tags
 * @member targetConnection - Full source connection details
 * @member assets - Map of assets within a project
 */

export const IAzureFields = {
    sasUrl: "https://prdapifunction.blob.core.windows.net/dcap?sv=2019-10-10&ss=bfqt&srt=sco&sp=rwdlacupx&se=2030-07-13T23:43:36Z&st=2020-07-13T15:43:36Z&spr=https&sig=wfqsJ4ekq78VnUG07q45YLKjBbTaZUB3S%2F%2BmppSlX1g%3D",
    endPoint: "https://prd-form-recognizer.cognitiveservices.azure.com/",
    key: "5de4a5bd45244e1b85cf62f0d4117d30",
    // sasUrl: "https://stjbsazfundata001.blob.core.windows.net/dcap?sv=2019-10-10&ss=bfqt&srt=sco&sp=rwdlacupx&se=2030-07-03T00:06:27Z&st=2020-07-02T16:06:27Z&spr=https&sig=64OUnSmZbeQn30pgzJWK6477OTeJHTfp%2FjS%2B6leh7%2BI%3D",
    // endPoint: "https://cs-fr-jbs-dcap-dev-001.cognitiveservices.azure.com/",
    // key: "62e96e0309014231ad1cdc07ee0e0597",
};


export const IAzureCVExportFields=
{
    apiKey : "7d70a928b45c4ee184f9dee2b1b33e9d",             
    domainId: "da2e3a8a-40a5-4171-82f4-58522f70fbc1"
}
/**
 * Queues interface
 */
export interface IQueues {
    date: string;
    description: string;
    files: string;
    project_name: string;
    queue_name: string;
}

export interface IProject {
    id: string;
    name: string;
    version: string;
    securityToken: string;
    description?: string;
    tags?: ITag[];
    tableTags: ITableTag[];
    sourceConnection: IConnection;
    assets?: { [index: string]: IAsset };
    lastVisitedAssetId?: string;
    apiUriBase: string;
    fromModel?: boolean;
    apiKey?: string | ISecureString;
    folderPath: string;

    //NEW
    predictModelId: string,
    analyzeResult :any, 

    //new
    exportFormat: IExportFormat;
   // videoSettings: IProjectVideoSettings;
    activeLearningSettings: IActiveLearningSettings;
    autoSave: boolean;
  //  sourceConnection: IConnection;
    targetConnection: IConnection;
    videoSettings: IProjectVideoSettings;

    projType?:string;
    cvProjId?:string;
    cvProjName?:string;
    trainingInfo?:any;

    isProjectExported:boolean;
    isProjectSaved:boolean;

    //new
    recentModelRecords?: IRecentModel[],
    apiVersion?: string;

}

/**
 * model interface
 */
export interface IUserModelsDetails {
    OCR: string;
    accuracy: any;
    created_date: string;
    hosted: string;
    labels?: [];
    model_id: string;
    project_name: string;
}

export interface IUploadFilesDetails {
    fileNames: any[];
    filesCount: number;
}

/**
 * @name - FileInfo
 * @description - Defines the file information and content for V1 projects
 * @member content - The content of a file (JSON string)
 * @member file - The File object point to the V1 project file
 */
export interface IFileInfo {
    content: string | ArrayBuffer;
    file: File;
}

/**
 * @name - Tag
 * @description - Defines the structure of a tag
 * @member name - User defined name
 * @member color - User editable color associated to tag
 */
export interface ITag {
    name: string;
    color: string;
    /*
    type: FieldType;
    columnTagName: string;
    tableFormat: boolean;
    format: FieldFormat;
    documentCount?: number,
    */

   type?: FieldType;
   columnTagName?: string;
   tableFormat?: boolean;
   format?: FieldFormat;
   documentCount?: number,
}

export interface ITableTag {
    name: string;
    // color: string;
    // type: FieldType;
    // format: FieldFormat;
}

export interface ITableColumnName {
    name: string;
}

export interface ITableRowName {
    name: string;
}

export interface ITagParams {
    projectName: string;
    projectId: string;
}

/**
 * @name - Connection
 * @description - Defines a reusable data source definition for projects
 * @member id - Unique identifier for connection
 * @member name - User defined name
 * @member description - User defined short description
 * @member providerType - The underlying storage type (Local File System, Azure Blob Storage, etc)
 * @member providerOptions - Provider specific options used to connect to the data source
 */
export interface IConnection {
    id: string;
    name: string;
    description?: string;
    providerType: string;
    providerOptions: IProviderOptions | ISecureString;
}

/**
 * @name - Asset
 * @description - Defines an asset within a project
 * @member id - Unique identifier for asset
 * @member type - Type of asset (Image, Video, etc)
 * @member name - Generated name for asset
 * @member path - Relative path to asset within the underlying data source
 * @member size - Size / dimensions of asset
 * @member format - The asset format (jpg, png, mp4, etc)
 */
export interface IAsset {
    id: string;
    type: AssetType;
    state: AssetState;
    name: string;
    path: string;
    size: ISize;
    labelData?: any;
    labelFilename?:string;
    ocrContent?: any;
    format?: string;
    status?: boolean;
    timestamp?: number;
    predicted?: boolean;
    ocr?: any;
    pdfBlob?: any;
    isDeleting?: boolean;
    isRunningOCR?: boolean;
    cachedImage?: string,

    
//new
parent?: IAsset;

 
labelingState?: AssetLabelingState,
 
isRunningAutoLabeling?: boolean,

mimeType?: string,

}

/**
 * @name - Asset Metadata
 * @description - Format to store asset metadata for each asset within a project
 * @member asset - References an asset within the project
 * @member regions - The list of regions drawn on the asset
 */
export interface IAssetMetadata {
    asset: IAsset;
    regions: IRegion[];
    version?: string;
    labelData?: ILabelData;
}

/**
 * @name - Size
 * @description - Defines the size and/or diminsion for an asset
 * @member width - The actual width of an asset
 * @member height - The actual height of an asset
 */
export interface ISize {
    width: number;
    height: number;
}

/**
 * @name - Region
 * @description - Defines a region within an asset
 * @member id - Unique identifier for this region
 * @member type - Defines the type of region
 * @member tags - Defines a list of tags applied to a region
 * @member points - Defines a list of points that define a region
 */
export interface IRegion {
    id: string;
    type: RegionType;
    category?: FeatureCategory;
    tags: string[];
    points?: IPoint[];
    boundingBox?: IBoundingBox;
    value?: string;
    pageNumber?: number;
    
    probability?:string;
    tagType?:string;
}

/**
 * @name - ILabelData
 * @description - Defines a label data correspond to an asset
 */
export interface ILabelData {
    
    document: string,
    labelingState?: AssetLabelingState;
    labels: ILabel[],
}

/**
 * @name - ILabel
 * @description - Defines a label
 */
export interface ILabel {
    label: string;
    key?: IFormRegion[];
    value: IFormRegion[];
    labelType?: string,
}

/**
 * @name - IFormRegion
 * @description - Defines a region which consumed by FormRecognizer
 */
export interface IFormRegion {
    page: number;
    text: string;
    boundingBoxes: [number[]];
}

/**
 * @name - Bounding Box
 * @description - Defines the tag usage within a bounding box region
 * @member left - Defines the left x boundary for the start of the bounding box
 * @member top - Defines the top y boundary for the start of the boudning box
 * @member width - Defines the width of the bounding box
 * @member height - Defines the height of the bounding box
 */
export interface IBoundingBox {
    left: number;
    top: number;
    width: number;
    height: number;
}

/**
 * @name - Point
 * @description - Defines a point / coordinate within a region
 * @member x - The x value relative to the asset
 * @member y - The y value relative to the asset
 */
export interface IPoint {
    x: number;
    y: number;
}

export interface ISecureString {
    encrypted: string;
}

export interface ISecurityToken {
    name: string;
    key: string;
}

export interface IField {
    fieldKey: string;
    fieldType: FieldType;
    fieldTableFormat: boolean;
    columnTagName: string;
    fieldFormat: FieldFormat;
}

export interface IFieldInfo {
    fields: IField[];
    cvProjId: string;
    cvProjName: string;
}

export interface IAuthenticationState {
    access_token?: string;
    error?: string;
    error_description?: string;
    expires_in?: number;
    id_token?: string;
    refresh_token?: string;
    status?: number;
    statusText?: string;
    token_type?: string;
    user?: IUser;

}

export interface IUser {
    emails?: string[];
    family_name?: string;
    given_name?: string;
    name?: string;
    newUser?: boolean;
    oid?: string;
    tenantId?: string;
    extension_appid: string;
    extension_role: string;
    extension_tenantuid: string;
}

/**
 * Enum of supported error codes
 */
export enum ErrorCode {
    // Note that the value of the enum is in camelCase while
    // the enum key is in Pascal casing
    Unknown = "unknown",
    GenericRenderError = "genericRenderError",
    ProjectInvalidJson = "projectInvalidJson",
    ProjectInvalidSecurityToken = "projectInvalidSecurityToken",
    ProjectDuplicateName = "projectDuplicateName",
    SecurityTokenNotFound = "securityTokenNotFound",
    OverloadedKeyBinding = "overloadedKeyBinding",
    BlobContainerIONotFound = "blobContainerIONotFound",
    BlobContainerIOForbidden = "blobContainerIOForbidden",
    PredictWithoutTrainForbidden = "predictWithoutTrainForbidden",
    ModelNotFound = "modelNotFound",
    ModelCountLimitExceeded = "modelCountLimitExceeded",
    HttpStatusUnauthorized = "unauthorized",
    HttpStatusUnauthorizedCode = 401,
    HttpStatusNotFound = "notFound",
    HttpStatusTooManyRequests = "tooManyRequests",
    ActiveLearningPredictionError = "activeLearningPredictionError",
    PasteRegionTooBig = "pasteRegionTooBig",
    ExportFormatNotFound = "exportFormatNotFound",
    
}

/**
 * @enum LOCAL - Local storage type
 * @enum CLOUD - Cloud storage type
 * @enum OTHER - Any other storage type
 */
export enum StorageType {
    Local = "local",
    Cloud = "cloud",
    Other = "other",
}

/**
 * @name - Asset Type
 * @description - Defines the type of asset within a project
 * @member Image - Specifies an asset as an image
 * @member PDF - Specifies an asset as a PDF
 * @member TIFF - Specifies an asset as a TIFF image
 */
export enum AssetType {
    Unknown = 0,
    Image = 1,
    PDF = 5,
    TIFF = 6,
    Video = 2,
    VideoFrame = 3,
    TFRecord = 4,
}

/**
 * @name - Asset State
 * @description - Defines the state of the asset with regard to the tagging process
 * @member NotVisited - Specifies as asset that has not yet been visited or tagged
 * @member Visited - Specifies an asset has been visited, but not yet tagged
 * @member Tagged - Specifies an asset has been visited and tagged
 */
export enum AssetState {
    NotVisited = 0,
    Visited = 1,
    Tagged = 2,
}

/**
 * @name - Region Type
 * @description - Defines the region type within the asset metadata
 * @member Square - Specifies a region as a square
 * @member Rectangle - Specifies a region as a rectangle
 * @member Polygon - Specifies a region as a multi-point polygon
 */
export enum RegionType {
    Polyline = "POLYLINE",
    Point = "POINT",
    Rectangle = "RECTANGLE",
    Polygon = "POLYGON",
    Square = "SQUARE",
}

export enum EditorMode {
    Rectangle = "RECT",
    Polygon = "POLYGON",
    Polyline = "POLYLINE",
    Point = "POINT",
    Select = "SELECT",
    CopyRect = "COPYRECT",
    None = "NONE",
}

export enum FieldType {
    String = "string",
    Number = "number",
    Date = "date",
    Time = "time",
    Integer = "integer",
    Checkbox = "checkbox",
    SelectionMark = "selectionMark",
}

export enum FieldFormat {
    NotSpecified = "not-specified",
    Currency = "currency",
    Decimal = "decimal",
    DecimalCommaSeparated = "decimal-comma-seperated",
    NoWhiteSpaces = "no-whitespaces",
    Alphanumeric = "alphanumeric",
    DMY = "dmy",
    MDY = "mdy",
    YMD = "ymd",
}

export enum FeatureCategory {
    Text = "text",
    Checkbox = "checkbox",
    Label = "label",
    DrawnRegion = "drawnRegion"
}

export enum ImageMapParent {
    Predict = "predict",
    Editor = "editor",
}

export enum LabelType {
    DrawnRegion = "drawnRegion"
}

export interface IResFunctions {
    message?: string;
    type?:string;
     
 }


/**
 * @name - Export Format
 * @description - Defines the settings for how project data is exported into commonly used format
 * @member id - Unique identifier for export format
 * @member name - Name of export format
 * @member providerType - The export format type (TF Records, YOLO, CSV, etc)
 * @member providerOptions - The provider specific option required to export data
 */
export interface IExportFormat {
    providerType: string;
    providerOptions: IExportProviderOptions | ISecureString;
}



/**
 * @name - Export Provider Options
 * @description - options defining the type of asset to export
 * @member assetState - export asset with the following state
 */
export interface IExportProviderOptions extends IProviderOptions {
    assetState: ExportAssetState;
}



/**
 * @name - TF Pascal VOC Records Export Asset State
 * @description - Defines the asset type export option
 * @member All - Specifies that all assets will be exported
 * @member Visited - Specifies that visited (including tagged) assets will be exported
 * @member Tagged - Specifies that only tagged assets will be exported
 */
export enum ExportAssetState {
    All = "all",
    Visited = "visited",
    Tagged = "tagged",
}



/**
 * @name - Active Learning Settings for the project
 * @description - Defines the active learning settings within a VoTT project
 * @member modelPathType - Model loading type ["coco", "file", "url"]
 * @member modelPath - Local filesystem path to the TF.js model
 * @member modelUrl - Web url to the TF.js model
 * @member autoDetect - Flag for automatically call the model while opening a new asset
 * @member predictTag - Flag to predict also the tag name other than the rectangle coordinates only
 */
export interface IActiveLearningSettings {
    modelPathType: ModelPathType;
    modelPath?: string;
    modelUrl?: string;
    autoDetect: boolean;
    predictTag: boolean;
}


/**
 * @name - Model Path Type
 * @description - Defines the mechanism to load the TF.js model for Active Learning
 * @member Coco - Specifies the default/generic pre-trained Coco-SSD model
 * @member File - Specifies to load a custom model from filesystem
 * @member Url - Specifies to load a custom model from a web server
 */
export enum ModelPathType {
    Coco = "coco",
    File = "file",
    Url = "url",
}



/**
 * @name - Video Tagging Settings for the project
 * @description - Defines the video settings within a VoTT project
 * @member frameExtractionRate - Extraction rate for a video (number of frames per second of video)
 */
export interface IProjectVideoSettings {
    frameExtractionRate: number;
}



//new


/**
 * Properties for additional project settings
 * @member activeLearningSettings - Active Learning settings
 */
export interface IAdditionalPageSettings extends IAssetPreviewSettings {
    activeLearningSettings: IActiveLearningSettings;
}



export interface ITFRecordMetadata {
    width: number;
    height: number;
    xminArray: number[];
    yminArray: number[];
    xmaxArray: number[];
    ymaxArray: number[];
    textArray: string[];
}

export interface ISAPOrder {
     
    endpoint_name?: string;
    project_name: string;    
    endpoint_url?: string;
    saporderdate?: string;
    invoiceNo:string;
    purchaseReq:string;
    status?:string;
    
 }

  
export interface IPrebuiltSettings{
    serviceURI: string;
    apiKey: string;
} 



/**
 * @name - Asset Labeling State
 * @description - Defines the labeling state for the asset
 * @member ManualLabeling - Specifies as an asset that has manual labeling the tags
 * @member Training - Specifies as an asset tagged data has been used for training model
 * @member AutoLabeling - Specifies as an asset that has run auto-labeling
 * @member AutoLabeledAndAdjusted -specifies as an asset that has run auto-labeling and tags manual adjusted
 */
export enum AssetLabelingState {
    ManuallyLabeled = 1,
    Trained = 2,
    AutoLabeled = 3,
    AutoLabeledAndAdjusted = 4,
}


export interface IRecentModel {
    readonly composedTrainResults?: object,
    readonly accuracies?: object,
    readonly averageAccuracy?: number,
    readonly modelInfo: {
        readonly isComposed: boolean,
        readonly modelId: string,
        readonly createdDateTime: string,
        readonly modelName: string,
    },
}



export const IChepIdList=
["nora.rouzes@chep.com","eesh.bagga@chep.com","kennedy.dillabaugh@chep.com",
"cass.brown@chep.com","mohammed.bhigjee@brambles.com","steve.kamta@chep.com",
"vijayak.in@mouritech.com","venkatakrishnap.in@mouritech.com","bryan.harper@chep.com",
"ed.jesus@brambles.com","mark.hagan@brambles.com",
"evi.schulz@chep.com","bailey.guinigundo@chep.com","rodney.johnson@chep.com",
"robbye.gould@chep.com","zoran.avramovic@chep.com","cynthia.mahoney@chep.com",
"rachel.curci@chep.com","rajt.in@mouritech.com","lokeshm.in@mouritech.com",
"fatima.fellah@chep.com","angie.ferreira@chep.com","kevin.chan@chep.com","eric.holt@chep.com",
"rosianne.berthelot@chep.com","tekeya.crenshaw@chep.com",	
"esperance.niyonemeza@chep.com",	
"annycherilla.arakaza@chep.com",	
"jea.deleon@chep.com",	
"jefka.loundja@chep.com",	
"linda.ndanze@chep.com",	
"stella.prieto@chep.com",	
"bradley.shaka@chep.com",	
"andres.villa@chep.com"	,"agatha.demarquez@chep.com",
"emmanuel.weli@chep.com",
"nadia.fathallah@chep.com",
"lorie.gateka@chep.com",
"stella.mboyo@chep.com"
];

export const IMChepIdList = [	
"vineelv.in@mouritech.com",	
"pranayap.in@mouritech.com",	
"sundarad.in@mouritech.com",	
"aditid@mouritech.com",	
"kavyak@mouritech.com",	
"nagave.in@mouritech.com",	
"roopsaik.in@mouritech.com",	
"venkatag.in@mouritech.com",	
"saivishwanthb.in@mouritech.com",	
"pavania.in@mouritech.com",	
"chandraa.in@mouritech.com",	
"vijayd.in@mouritech.com",	
"jyothim.in@mouritech.com",	
"vikasm.in@mouritech.com",	
"harikamu.in@mouritech.com",	
"vibhab.in@mouritech.com",	
"madank.in@mouritech.com",	
"vinayka.in@mouritech.com",	
"nagako.in@mouritech.com",	
"aleems.in@mouritech.com",
"anishs.in@mouritech.com",
"ratann.in@mouritech.com",
"pujarim.in@mouritech.com","pavanip.in@mouritech.com","sindhud.in@mouritech.com",
"jessem.in@mouritech.com"
]



export interface IBusinessUser {
    userid?:string;
    features?:[];
    role:string;
}

     